import { isElementScrollbarVisible, useScrollListener } from '@helpers';
import { useEntitiesTableData } from '@terminal/react-query-hooks/entities/useEntities';
import { type RefObject, useLayoutEffect, useMemo } from 'react';

export const SCROLL_PAGINATED_ENTITIES_PAGE_SIZE = 20;

type UseScrollPaginatedEntitiesOptions = {
  scrollContainerRef?: RefObject<HTMLDivElement | null>;
  scrollFillKey?: number;
  pageSize?: number;
};

export const useScrollPaginatedEntities = (
  tags: string[],
  enabled: boolean,
  {
    scrollContainerRef,
    scrollFillKey = 0,
    pageSize = SCROLL_PAGINATED_ENTITIES_PAGE_SIZE,
  }: UseScrollPaginatedEntitiesOptions = {}
) => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useEntitiesTableData(enabled ? tags : [], pageSize);

  const pages = data?.pages;
  const entities = useMemo(() => (pages ?? []).flat().filter(Boolean), [pages]);

  useScrollListener(
    scrollContainerRef ?? { current: null },
    () => {
      if (hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }

      return hasNextPage;
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
    { isGlobalScrollListened: false, offset: 20 }
  );

  useLayoutEffect(() => {
    if (!enabled || !scrollContainerRef?.current || !hasNextPage) {
      return;
    }

    if (isFetchingNextPage) {
      return;
    }

    const hasScroll = isElementScrollbarVisible(scrollContainerRef, 'vertical');

    if (hasScroll) {
      return;
    }

    void fetchNextPage();
  }, [
    enabled,
    scrollContainerRef,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    scrollFillKey,
  ]);

  return {
    entities,
    loadedPageCount: pages?.length ?? 0,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  };
};
